import { Grid } from '@mui/material';
import _ from 'lodash';
import { OnChangeValue } from 'react-select';
import AsyncSelect from 'react-select/async';

import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import { planService } from '../../../../application/PlanService';
import SearchLensIcon from '../../../../assets/svg/SearchLensIcon';
import { getPlanStatusColor } from '../../../../constants/Colors';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../react-components/Text';
import { PlanStatusIcon } from '../../shared/PlanStatusIcon';
import { useManagePlanQuery } from '../hooks/useManagePlanQuery';

const OPTIONS_LIMIT = 15;

export const PlansSearcher = () => {
  const { navigateToPlan } = useManagePlanQuery();
  const { data: project } = useRetrieveActiveProject();

  const _loadSuggestions = (inputValue: string, callback: (options: PlanFlat[]) => void) => {
    if (inputValue === ``) {
      callback([]);
      return;
    }

    planService
      .search({
        project: project!,
        limit: OPTIONS_LIMIT,
        sort_by: `execution_date`,
        predicates: [
          SearchPredicate.create({
            field: `label`,
            operator: `contains`,
            type: `string`,
            value: inputValue,
          }),
        ],
      })
      .then((resp) => callback(resp));
  };

  const loadSuggestions = _.debounce(_loadSuggestions, 300);

  return (
    <div
      style={{
        width: `400px`,
        background: `${RoutalPalette.neutral02n}`,
      }}
    >
      <AsyncSelect
        cacheOptions
        isClearable
        isSearchable
        styles={{
          // input: (base) => ({
          //   fontSize: `13px`,
          // }),
          // placeholder: (base, state) => ({
          //   ...base,
          //   color: state.isFocused ? RoutalPalette.primary80 : RoutalPalette.secondary00,
          // }),
          // input(base, props) {
          //   ...base,
          // }),
          control: (base, state) => ({
            ...base,
            cursor: `pointer`,
            color: RoutalPalette.text,
            fontSize: `13px`,
            boxShadow: `none`,
            borderColor: state.isFocused
              ? `${RoutalPalette.primary00} !important`
              : `${RoutalPalette.neutral20} !important`,
            backgroundColor: `transparent`,
            '&:hover': {
              backgroundColor: RoutalPalette.neutral05, // replace with your desired hover color
            },
          }),
          menu: (base) => ({
            ...base,
            color: RoutalPalette.text,
            backgroundColor: RoutalPalette.neutral02n,
            zIndex: 2,
          }),
          clearIndicator: (base) => ({
            ...base,
            color: RoutalPalette.secondary60,
          }),
          indicatorSeparator: (base) => ({
            display: `none`,
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            display: `none`,
          }),
          valueContainer(base, props) {
            return {
              ...base,
              display: `flex`,
            };
          },
        }}
        components={{
          // Input: ({ innerProps, isFocused }) => (
          //   <input
          //     {...innerProps}
          //     style={{
          //       fontSize: `13px`,
          //       color: isFocused ? RoutalPalette.primary80 : RoutalPalette.secondary00,
          //     }}
          //   />
          // ),
          Placeholder: ({ innerProps, isFocused, children }) => (
            <div
              style={{
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                gap: `4px`,
                flexWrap: `nowrap`,
              }}
              {...innerProps}
            >
              <SearchLensIcon />

              {!isFocused && <Text style={{ cursor: `pointer` }} intlId="planner.plans.search_ph" />}
            </div>
          ),
          Option: ({ innerProps, isDisabled, data }) => (
            <div
              {...innerProps}
              key={data.id}
              style={{
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                flexWrap: `nowrap`,
                gap: `8px`,
                userSelect: `none`,
                height: `50px`,
                padding: `8px 12px`,
                cursor: `pointer`,
              }}
            >
              <Grid
                container
                direction="column"
                wrap="nowrap"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: `unset`,
                  borderRadius: `5px`,
                  backgroundColor: getPlanStatusColor(data.status, `light`),
                  border: `1px solid ${getPlanStatusColor(data.status, `medium`)}`,
                  padding: `6px 10px`,
                }}
              >
                <PlanStatusIcon
                  status={data.status}
                  style={{ color: getPlanStatusColor(data.status, `dark`), width: `12px` }}
                />
              </Grid>

              <Grid container direction="column" wrap="nowrap" gap="2px" style={{ width: `calc(100% - 34px)` }}>
                {data.label !== `` ? (
                  <RoutalTooltip id="plan-id" placement="top" title={data.label!}>
                    <Text
                      variant="body"
                      style={{
                        cursor: `pointer`,
                        textOverflow: `ellipsis`,
                        overflow: `hidden`,
                        whiteSpace: `nowrap`,
                      }}
                    >
                      {data.label}
                    </Text>
                  </RoutalTooltip>
                ) : (
                  <Text
                    variant="body"
                    style={{
                      cursor: `pointer`,
                      fontWeight: 500,
                      color: RoutalPalette.secondary00,
                    }}
                    intlId="no_name"
                  />
                )}

                <Text tagName="span" variant="label" style={{ cursor: `pointer` }}>
                  {dateFormatShortDate(data.execution_date)}
                </Text>
              </Grid>
            </div>
          ),
          NoOptionsMessage: (_props: any) => (
            <div
              {..._props}
              style={{
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                flexWrap: `nowrap`,
                userSelect: `none`,
                height: `50px`,
                padding: `8px 12px`,
              }}
            >
              <Text intlId="no_results" />
            </div>
          ),
          LoadingMessage: (_props: any) => (
            <div
              {..._props}
              style={{
                display: `flex`,
                flexDirection: `row`,
                alignItems: `center`,
                flexWrap: `nowrap`,
                userSelect: `none`,
                height: `50px`,
                padding: `8px 12px`,
              }}
            >
              <Text intlId="loading" />
            </div>
          ),
        }}
        loadOptions={loadSuggestions}
        onChange={(newValue: OnChangeValue<PlanFlat, false>) => {
          if (!newValue) return;
          navigateToPlan({ planId: newValue.id });
        }}
        // onInputChange={_.debounce((inputValue) => {
        //   console.info(`inputValue`, inputValue);
        //   setSearchString(inputValue);
        // }, 300)}
        // loadOptions={loadSuggestions}
      />
    </div>
  );
};
